import { inject, injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import type { IQueryClient, TQueryClientFactory } from "@workspace/types";
import { getPublicDemoRecap } from "../api/training-sessions";
import { LandingServiceID } from "./symbols";
import type {
  IDemoRecapManager,
  PublicDemoRecap,
} from "@/lib/types/demo-session";

const ANALYSIS_POLL_MS = 3000;
// The backend retries a failed analysis forever, so the client bounds the wait.
const ANALYSIS_POLL_BUDGET_MS = 3 * 60 * 1000;

@injectable()
export class DemoRecapStore implements IDemoRecapManager {
  private sessionId = "";
  private pollDeadline = 0;
  private recapQuery: IQueryClient<PublicDemoRecap>;

  constructor(
    @inject(LandingServiceID.QueryClientFactory)
    createQuery: TQueryClientFactory,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.recapQuery = createQuery<PublicDemoRecap>(() => ({
      queryKey: ["demo-recap", this.sessionId],
      queryFn: () => getPublicDemoRecap(this.sessionId),
      enabled: !!this.sessionId,
      retry: false,
      refetchInterval: (query) =>
        query.state.data?.analysis_status === "pending" &&
        query.state.dataUpdatedAt < this.pollDeadline
          ? ANALYSIS_POLL_MS
          : false,
    }));
  }

  get recap(): PublicDemoRecap | null {
    return this.recapQuery.result.data ?? null;
  }

  get isLoading(): boolean {
    return this.recapQuery.result.isPending;
  }

  get isError(): boolean {
    return this.recapQuery.result.isError;
  }

  get isTimedOut(): boolean {
    return (
      this.recap?.analysis_status === "pending" &&
      this.recapQuery.result.dataUpdatedAt >= this.pollDeadline
    );
  }

  get isAnalysisPending(): boolean {
    return this.recap?.analysis_status === "pending" && !this.isTimedOut;
  }

  setSessionId(id: string): void {
    runInAction(() => {
      this.pollDeadline = Date.now() + ANALYSIS_POLL_BUDGET_MS;
      this.sessionId = id;
    });
  }

  refetch(): void {
    this.pollDeadline = Date.now() + ANALYSIS_POLL_BUDGET_MS;
    this.recapQuery.refetch();
  }
}
